import React, { useCallback, useEffect, useState } from "react";
import styles from "./QuoatesCard.module.scss";
import {
  ZitateGridOptionsType,
  ZitateSliderOptionsType,
} from "@/types/ModuleOptionsType";
import { customSanitize } from "../../utils/customSanitize";
import Image from "next/image";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { QuotesElementType } from "@/types/QuoteType";
import { has } from "lodash";
import { UserImage } from "./UserImage/UserImage";

import * as motion from "motion/react-m";
import { Variants } from "motion/react";
import { useCarousel } from "@aleksundshantu/aunds-utils-react/primitives/carousel";
import { SVGFromURL } from "../SVGFromURL/SVGFromURL";
export type QuoatesCardOptions = ZitateSliderOptionsType &
  ZitateGridOptionsType;

interface QuoatesCardProps {
  quotesOptions: QuoatesCardOptions;
  element: QuotesElementType;
  showAvatar?: boolean;
  showRating?: boolean;
  cardIndex: number;
  moduleIndex: number;
  cardType: "slider" | "grid";
  headling_level: number;
  variants?: Variants;
}

export const QuoatesCard = ({
  element,
  quotesOptions,
  showAvatar = false,
  showRating = false,
  cardIndex,
  moduleIndex,
  cardType,
  headling_level,
  variants,
}: QuoatesCardProps) => {
  const {
    max_character,
    box: { show_border_radius, show_box_shadow, show_card_box },
  } = quotesOptions;

  const {
    title,
    content,
    firma,
    rating_stars: ratingStars,
    name,
    position,
    source,
  } = element;
  const maxChar = has(max_character, "small_min_characters")
    ? max_character.small_min_characters || 0
    : 0;
  const textClass = content.length >= maxChar ? "" : "quotes-slider-big";
  const hasSource =
    (source?.type === "logo" && source.logo) ||
    (source?.type === "text" && source.text);

  const QuotesJSX = (
    <>
      <span className={"quotes-name is-block"}>{name}</span>
      <span className={"quotes-source-position is-block"}>
        {position}
        {position && firma && ", "}
        {firma}
      </span>
    </>
  );

  const { getIcon } = useGlobalContext();
  const image = getIcon("avatar");

  /* jscpd:ignore-start */
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const { emblaApi } = useCarousel();

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const activeIndex = emblaApi.selectedScrollSnap();
    setActiveIndex(activeIndex);
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);
  const animation =
    cardType === "slider"
      ? {
          initial: "initial",
          animate: activeIndex === cardIndex ? "animate" : "exit",
        }
      : {};
  /* jscpd:ignore-end */
  return (
    <motion.article
      variants={variants}
      {...animation}
      data-card-type={cardType}
      className={`${styles["quote-card"]} ${cardType === "slider" ? (!image || !showAvatar ? styles["no-image"] : "") : styles["no-image"]} quote-card ${show_card_box ? "is-box" : ""} ${show_border_radius && show_card_box ? "has-border-radius" : ""} ${show_box_shadow && show_card_box ? "has-box-shadow" : ""} ${ratingStars && showRating ? `${styles["has-rating"]}` : ""} ${hasSource ? styles["has-source"] : ""}`}
    >
      <div className="h-100 is-flex is-flex-direction-column">
        <header>
          {cardType === "grid" && (
            <div className="is-flex is-justify-content-space-between is-align-items-center">
              {ratingStars && showRating && (
                <Stars
                  rating={ratingStars}
                  cardIndex={cardIndex}
                  moduleIndex={moduleIndex}
                  extraClass="shrink-0"
                />
              )}

              <SourceDisplay source={source} extraClass="shrink-0" />
            </div>
          )}
          {image && showAvatar && cardType === "slider" && (
            <UserImage data-card-type={cardType} image={image} />
          )}
          {cardType === "slider" && (
            <>
              {ratingStars && showRating && (
                <Stars
                  rating={ratingStars}
                  cardIndex={cardIndex}
                  moduleIndex={moduleIndex}
                />
              )}
              <SourceDisplay source={source} />
            </>
          )}
        </header>

        <div className={styles["quote-wrapper"]}>
          <section className={styles["text-wrapper"]}>
            <SVGFromURL src={getIcon("quote")} width={43} height={34} />
            <p
              role="heading"
              aria-level={headling_level}
              className={`${styles["quote-title"]} title`}
            >
              {title}
            </p>
            <blockquote
              className={`p ${styles["blockquote"]} ${textClass ? textClass : ""}`}
              dangerouslySetInnerHTML={{ __html: customSanitize(content) }}
            />
          </section>

          <footer className={styles["user-info"]}>
            {image && showAvatar && cardType === "grid" && (
              <UserImage data-card-type={cardType} image={image} />
            )}

            {cardType === "slider" ? QuotesJSX : <div>{QuotesJSX}</div>}
          </footer>
        </div>
      </div>
    </motion.article>
  );
};

const SourceLogo: React.FC<{ logo: QuotesElementType["source"]["logo"] }> = ({
  logo,
}) => {
  if (!logo) return null;
  return (
    <div className={styles["source-logo"]}>
      <Image
        src={logo.url}
        alt={logo.alt}
        width={logo.width}
        height={logo.height}
        style={{ objectFit: "cover" }}
        sizes="25vw"
        // blurDataURL={`data:${logo.mime_type}${logo.image_blur}`}
        // placeholder="blur"
      />
    </div>
  );
};

const SourceText: React.FC<{
  text: QuotesElementType["source"]["text"];
}> = ({ text }) => <div className={"quotes-source-position"}>{text}</div>;

const SourceDisplay: React.FC<{
  source: QuotesElementType["source"];
  extraClass?: string;
}> = ({ source, extraClass = "" }) => {
  if (
    !source ||
    (source?.type === "logo" && !source?.logo) ||
    (source?.type === "text" && !source?.text)
  ) {
    return null;
  }

  return (
    <div className={`${styles.source} ${extraClass}`}>
      {source.type === "logo" && source.logo && (
        <SourceLogo logo={source.logo} />
      )}
      {source.type === "text" && source.text && (
        <SourceText text={source.text} />
      )}
    </div>
  );
};
const StarIcon = ({
  fillPercentage,
  starIndex,
  color = "currentColor", // Define un color predeterminado o usa el color pasado como prop
  cardIndex,
  moduleIndex,
}: {
  fillPercentage: number;
  starIndex: number;
  color?: string;
  cardIndex: number;
  moduleIndex: number;
}) => {
  const gradientId = `partial-fill-${moduleIndex}-${cardIndex}-${starIndex}`;

  return (
    <svg
      viewBox="0 0 24 24"
      className={styles.star}
      aria-hidden="true"
      width="24"
      height="24"
    >
      <defs>
        <linearGradient id={gradientId}>
          <stop offset={`${fillPercentage}%`} stopColor={color} />
          <stop offset={`${fillPercentage}%`} stopColor="transparent" />
        </linearGradient>
      </defs>
      <path
        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
        fill="none"
        stroke={color}
      />
    </svg>
  );
};

interface StarsProps {
  rating: number;
  moduleIndex: number;
  cardIndex: number;
  extraClass?: string;
}

const Stars = ({
  rating,
  moduleIndex,
  cardIndex,
  extraClass = "",
}: StarsProps) => {
  const fullStars = Math.floor(rating);
  const partialStarPercentage = (rating - fullStars) * 100;
  const emptyStars = 5 - Math.ceil(rating);
  const { getLabel } = useGlobalContext();
  const label = getLabel("rating_label").replace("$rating", rating.toString());

  return (
    <span
      role="img"
      aria-label={label}
      className={`${styles.rating} ${extraClass}`}
    >
      <span className={`is-flex`} aria-hidden="true">
        {Array.from({ length: fullStars }, (_, i) => (
          <StarIcon
            key={`full-${i}`}
            fillPercentage={100}
            starIndex={i}
            color={"var(--quotes-slider-stars-color)"}
            cardIndex={cardIndex}
            moduleIndex={moduleIndex}
          />
        ))}
        {partialStarPercentage > 0 && (
          <StarIcon
            fillPercentage={partialStarPercentage}
            starIndex={fullStars}
            color={"var(--quotes-slider-stars-color)"}
            cardIndex={cardIndex}
            moduleIndex={moduleIndex}
          />
        )}
        {Array.from({ length: emptyStars }, (_, i) => (
          <StarIcon
            key={`empty-${i}`}
            fillPercentage={0}
            starIndex={fullStars + i + 1}
            color={"var(--quotes-slider-stars-color)"}
            cardIndex={cardIndex}
            moduleIndex={moduleIndex}
          />
        ))}
      </span>
    </span>
  );
};
